import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, MessageSquarePlus, UserPlus, Users, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface Conversation {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface Profile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface ContactListProps {
  conversations: Conversation[];
  selectedConversation: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation?: () => void;
}

export function ContactList({
  conversations,
  selectedConversation,
  onSelectConversation,
  onNewConversation,
}: ContactListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [followers, setFollowers] = useState<Profile[]>([]);
  const [following, setFollowing] = useState<Profile[]>([]);
  const [activeTab, setActiveTab] = useState('messages');
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadFollowers();
      loadFollowing();
    }
  }, [user]);

  const loadFollowers = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('follows')
      .select('follower_id')
      .eq('following_id', user.id);

    if (!error && data) {
      const followerIds = data.map(f => f.follower_id);
      if (followerIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', followerIds);
        
        if (profilesData) {
          setFollowers(profilesData);
        }
      }
    }
  };

  const loadFollowing = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);

    if (!error && data) {
      const followingIds = data.map(f => f.following_id);
      if (followingIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', followingIds);
        
        if (profilesData) {
          setFollowing(profilesData);
        }
      }
    }
  };

  const filteredConversations = conversations.filter(
    c =>
      c.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.display_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const searchUsers = async () => {
    if (!userSearch.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url')
      .or(`username.ilike.%${userSearch}%,display_name.ilike.%${userSearch}%`)
      .limit(10);

    setIsSearching(false);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to search users',
        variant: 'destructive',
      });
      return;
    }

    setSearchResults(data || []);
  };

  const startConversation = (userId: string) => {
    onSelectConversation(userId);
    onNewConversation?.();
    setUserSearch('');
    setSearchResults([]);
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const renderUserList = (users: Profile[], emptyMessage: string) => {
    const filteredUsers = users.filter(
      u =>
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.display_name && u.display_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (filteredUsers.length === 0) {
      return (
        <div className="p-8 text-center">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="divide-y">
        {filteredUsers.map((profile) => (
          <div
            key={profile.id}
            onClick={() => startConversation(profile.id)}
            className="p-4 cursor-pointer hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={profile.avatar_url || ''} />
                <AvatarFallback>
                  {(profile.display_name || profile.username)[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">
                  {profile.display_name || profile.username}
                </p>
                <p className="text-xs text-muted-foreground">
                  @{profile.username}
                </p>
              </div>
              <MessageSquarePlus className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Messages</h2>
          <Dialog>
            <DialogTrigger asChild>
              <Button size="icon" variant="outline">
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start New Conversation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search users by username..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchUsers();
                      }
                    }}
                  />
                  <Button onClick={searchUsers} disabled={isSearching}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-[300px]">
                  {searchResults.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      {userSearch ? 'No users found' : 'Search for users to start a conversation'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map((profile) => (
                        <div
                          key={profile.id}
                          onClick={() => startConversation(profile.id)}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                        >
                          <Avatar>
                            <AvatarImage src={profile.avatar_url || ''} />
                            <AvatarFallback>
                              {(profile.display_name || profile.username)[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-semibold">
                              {profile.display_name || profile.username}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              @{profile.username}
                            </p>
                          </div>
                          <UserPlus className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-2 grid w-auto grid-cols-3">
          <TabsTrigger value="messages" className="gap-2">
            <MessageSquarePlus className="h-4 w-4" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="followers" className="gap-2">
            <Heart className="h-4 w-4" />
            Fans ({followers.length})
          </TabsTrigger>
          <TabsTrigger value="following" className="gap-2">
            <Users className="h-4 w-4" />
            Following ({following.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="messages" className="flex-1 mt-0">
          <ScrollArea className="h-full">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center space-y-4">
                <div className="flex justify-center">
                  <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
                    <MessageSquarePlus className="h-10 w-10 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">No conversations yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Start a new conversation to connect with other users
                  </p>
                </div>
              </div>
            ) : (
              <div className="divide-y">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => onSelectConversation(conversation.id)}
                    className={`p-4 cursor-pointer hover:bg-accent transition-colors ${
                      selectedConversation === conversation.id ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <Avatar>
                          <AvatarImage src={conversation.avatar_url} />
                          <AvatarFallback>
                            {conversation.display_name[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {conversation.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 h-5 w-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                            {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <p className="font-semibold truncate">
                            {conversation.display_name}
                          </p>
                          {conversation.last_message_time && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatTime(conversation.last_message_time)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          @{conversation.username}
                        </p>
                        <p className={`text-sm truncate ${
                          conversation.unread_count > 0 
                            ? 'font-semibold text-foreground' 
                            : 'text-muted-foreground'
                        }`}>
                          {conversation.last_message || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="followers" className="flex-1 mt-0">
          <ScrollArea className="h-full">
            {renderUserList(followers, 'No fans yet')}
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="following" className="flex-1 mt-0">
          <ScrollArea className="h-full">
            {renderUserList(following, 'Not following anyone yet')}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
